import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Record } from '../../../config/Record/RecordEntity';
import { t, translateDate } from '../../../components/common/Translate';
import { RECORD_STATUS } from '../../../config';
import { Auth, recordManager } from '../../../config/configuration/react';

export const useAppointment = (recordId) => {
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState({
    homeHBS: false,
    phoneHBS: false,
    counterApp: false,
    ford: false,
    clic2buyFile: false,
  });

  const isRecordLoading = useSelector((state) => state.reduxRecords.loading);
  const errorRecordApi = useSelector((state) => state.reduxRecords.error);
  const appointment = useSelector((state) => state.reduxRecords.data[recordId])?.appointment || {};
  const fileNumber = useSelector((state) => state.reduxRecords.data[recordId])?.record?.refHexaId || '';
  const pointOfSaleName = useSelector((state) => state.reduxRecords.data[recordId])?.carcheckPointOfSale?.name || '';
  const pointOfSaleId = useSelector((state) => state.reduxRecords.data[recordId])?.carcheckPointOfSale?.id || null;
  const expertise = useSelector((state) => state.reduxRecords.data[recordId])?.expertise || {};
  const fordGuaranteeReference = useSelector((state) => state.reduxRecords.data[recordId])?.coverage?.reference || null;
  const record = useSelector((state) => state.reduxRecords.data[recordId]) || {};
  let status = useSelector((state) => state.reduxRecords.data[recordId])?.record?.statusId;
  const nrmStatusId = useSelector((state) => state.reduxRecords.data[recordId])?.record?.nrmStatusId;
  const recordEntity = new Record(record);
  const registration = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.registration);
  const { expertise_finished, expertise_authorized, supervisor_refused, refused, report } = RECORD_STATUS;
  const [recordList, setRecordList] = useState([]);
  const counterInspection = useSelector((state) => state.reduxRecords.data[recordId])?.counterInspection || {};
  const guaranteedPrice = useSelector((state) => state.reduxRecords.data[recordId])?.guaranteedPrice || {};
  const buyers = useSelector((state) => state.reduxRecords.data[recordId])?.buyers || [];
  const [isOpenGuaranteeId, setIsOpenGuaranteeId] = useState(false);
  const [buyerRecordName, setBuyerName] = useState(null);
  const [updateBuyer, setUpdateBuyer] = useState({
    loading: false,
    error: false,
  });

  const showModelCounterInspaction = status !== 18 && recordEntity.type() === 2;
  const openInfoBull = (target) => {
    const res = { ...tooltipOpen };
    res[target] = !res[target];
    setTooltipOpen(res);
  };

  const onClickGuaranteeId = () => setIsOpenGuaranteeId((prev) => !prev);
  const hideStatus = [
    RECORD_STATUS.record_init,
    RECORD_STATUS.expertise_restarted,
    RECORD_STATUS.appointment_planned,
    RECORD_STATUS.closed,
    RECORD_STATUS.report_ongoing,
    RECORD_STATUS.old_flow,
  ];

  const toggleEditModal = () => setEditModalIsOpen((s) => !s);
  const toggleDeleteModal = () => setDeleteModalIsOpen((s) => !s);

  const { appointmentDateTime } = appointment;
  if (appointmentDateTime) {
    _.set(appointment, 'appointmentDate', translateDate(appointmentDateTime, 'dddd DD MMM YYYY'));
    _.set(appointment, 'appointmentTime', translateDate(appointmentDateTime, 'LT'));
  }
  status = !hideStatus.includes(status) ? status : null;

  if (status === 3 && recordEntity.type() === 2) status = 'counterAppraisalDone';
  const buyerName = `${expertise?.buyerFirstName || ''} ${expertise?.buyerLastName || ''}`;

  const checkUsedImmat = async () => {
    try {
      const query = {
        registration,
        status: [expertise_finished, expertise_authorized, supervisor_refused, refused, report],
        range: [0, 9],
        sourceIds: Auth.sourceIds().join(','),
        verifAccess: 'false',
        recordTypeId: 1,
      };

      const { records } = await recordManager.search(query);
      let recordsTotal = [...records];
      if (counterInspection?.refHexaId) {
        const { records: recordsPlus } = await recordManager.search({
          refHexaId: counterInspection?.refHexaId,
          range: [0, 9],
          verifAccess: 'false',
          recordTypeId: 1,
        });
        recordsTotal = _.unionBy(records, recordsPlus, 'recordId');
      }
      if (_.isEmpty(recordsTotal)) {
        throw new Error('empty datas');
      }
      setRecordList(recordsTotal);
    } catch (error) {
      console.log('error checkUsedImmat', error);
    }
  };

  useEffect(() => {
    if (recordEntity.type() === 2 && registration) checkUsedImmat();
  }, [registration, counterInspection]);
  const chnageBuyerName = async (value) => {
    setUpdateBuyer({ loading: true, error: false });
    setBuyerName({ value: value?.value, label: t(value?.label) });
    const body = {
      concessionId: expertise?.concessionId,
      expertiseBuyerId: value?.value,
      recordTypeId: recordEntity.type(),
      refHexaId: record?.record?.refHexaId,
    };
    try {
      await recordManager.updateBuyerExpertise(recordId, body);
    } catch (e) {
      setUpdateBuyer((perv) => ({ ...perv, error: true }));
    } finally {
      setUpdateBuyer((perv) => ({ ...perv, loading: false }));
    }
  };

  useEffect(
    () =>
      setBuyerName({
        value: expertise?.expertiseBuyerId,
        label: t(`${expertise?.buyerFirstName || ''} ${expertise?.buyerLastName || ''}`),
      }),
    [expertise?.expertiseBuyerId],
  );
  return {
    appointment,
    fileNumber,
    pointOfSaleName,
    pointOfSaleId,
    buyerFullName: buyerName.trim(),
    isRecordLoading,
    errorRecordApi,
    isHBS: recordEntity.isHBS(),
    formSource: recordEntity.formSource(),
    allowDelete: recordEntity.cancelIsAllowed(),
    allowEdit:
      recordEntity.editAppointmentIsAllowed() ||
      (recordEntity.type() === 2 && !recordEntity.isNrmRecord() && recordList.length > 0),
    allowVinCheck: false,
    isLoadingVin: false,
    status,
    toggleEditModal,
    editModalIsOpen,
    toggleDeleteModal,
    deleteModalIsOpen,
    openInfoBull,
    tooltipOpen,
    recordTypeId: recordEntity.type(),
    fordGuaranteeReference,
    records: recordList,
    role: Auth.role(),
    counterInspection,
    showModelCounterInspaction,
    isNrmRecord: recordEntity.isNrmRecord(),
    guaranteedPrice,
    isOpenGuaranteeId,
    onClickGuaranteeId,
    nrmStatusId,
    buyers,
    buyerRecordName,
    chnageBuyerName,
    updateBuyer,
    allowedChangeBuyer: Auth.isAllowedToChangeBuyer(),
  };
};
